import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "@/i18n/use-translation";
import { cn } from "@/lib/utils";
import { selectPulse, type PulseCell } from "@/lib/pulse";
import type { WasteLineStats } from "@/hooks/use-line-stats";
import { foodLabel } from "@/lib/food-labels";

type Props = {
  /** First line-stats load — before any data, show a skeleton, not the hidden state. */
  isLoading: boolean;
  lines: WasteLineStats[] | undefined;
  /** Sets The Proof feed filter to the clicked line (The Proof scrolls itself in). */
  onViewCaptures: (cell: PulseCell) => void;
};

/**
 * Position 1 of the homepage core (POS2-871): one line to fix, one that worked,
 * each backed by capture counts. "View captures" narrows The Proof to that line.
 * Hidden entirely when the site has no captures in either period.
 */
export function PulseStrip({ isLoading, lines, onViewCaptures }: Props) {
  if (isLoading && lines == null) return <PulseSkeleton />;

  const pair = selectPulse(lines ?? []);
  if (!pair) return null;

  const cells: { tone: "bad" | "good"; labelKey: string; cell: PulseCell }[] = [];
  if (pair.needsChanging)
    cells.push({ tone: "bad", labelKey: "dashboard.pulseNeedsChanging", cell: pair.needsChanging });
  if (pair.working)
    cells.push({ tone: "good", labelKey: "dashboard.pulseWorking", cell: pair.working });

  return (
    <div
      className={cn("grid gap-3.5", cells.length > 1 && "sm:grid-cols-2")}
      data-testid="pulse-strip"
    >
      {cells.map(({ tone, labelKey, cell }) => (
        <PulseCellView
          key={cell.lineKey}
          tone={tone}
          labelKey={labelKey}
          cell={cell}
          onView={() => onViewCaptures(cell)}
        />
      ))}
    </div>
  );
}

function PulseCellView({
  tone,
  labelKey,
  cell,
  onView,
}: {
  tone: "bad" | "good";
  labelKey: string;
  cell: PulseCell;
  onView: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-3 rounded-xl border border-l-[3px] border-gray-200/80 bg-white px-4 py-3 shadow-[0_1px_2px_rgba(17,24,39,0.04),0_1px_3px_rgba(17,24,39,0.06)]",
        tone === "bad" ? "border-l-red-500" : "border-l-green-600",
      )}
      data-testid={`pulse-cell-${tone}`}
    >
      <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-gray-400">
        {t(labelKey)}
      </span>
      {/* The cell truncates, so the tooltip is the only way to read the whole name — it has to
          be the same language as the label above it (POS2-1158). */}
      <span
        className="min-w-0 flex-1 truncate text-sm font-bold text-gray-900"
        title={foodLabel(t, cell.label)}
      >
        {foodLabel(t, cell.label)}
      </span>
      {/* Secondary detail: only where the cell is wide enough to keep one line
          (narrow / 2-col breakpoints would overflow) — the item truncates first. */}
      <span className="hidden shrink-0 whitespace-nowrap text-xs text-gray-500 tabular-nums xl:inline">
        {t("dashboard.pulseCapturesVsPrev", {
          captures: cell.captures,
          previous: cell.prevCaptures,
        })}
      </span>
      <button
        type="button"
        onClick={onView}
        aria-label={t("dashboard.pulseViewCaptures")}
        className={cn(
          "shrink-0 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors",
          tone === "bad"
            ? "border-red-200 bg-red-50 text-red-700 hover:border-red-600 hover:bg-red-600 hover:text-white"
            : "border-green-200 bg-green-50 text-green-700 hover:border-green-700 hover:bg-green-700 hover:text-white",
        )}
        data-testid="pulse-view-captures"
      >
        {t("dashboard.pulseView")}
        <span className="hidden xl:inline">{t("dashboard.pulseViewSuffix")}</span> →
      </button>
    </div>
  );
}

function PulseSkeleton() {
  return (
    <div className="grid gap-3.5 sm:grid-cols-2" data-testid="pulse-skeleton">
      {Array.from({ length: 2 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-xl border border-l-[3px] border-gray-200/80 border-l-gray-200 bg-white px-4 py-3"
        >
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-6 w-24 rounded-full" />
        </div>
      ))}
    </div>
  );
}
